import {
  AssignedUserSelection,
  CreateCshGroupRequest,
  CshGroup,
  CshGroupAlarm,
  CshGroupDetail,
  CshGroupSite,
  CshGroupSummary,
  CshSidebarGroup,
  GroupDetails,
  UpdateCshGroupRequest,
} from "./csh.types";

import { apiClient } from "../apiClient";
import type { ApiResponse } from "../http/types";
import apiService from "../apiService";

const USERS_URL = "/api/users";
const CUSTOMER_URL = "/api/Customer";
const BASE = "/api/csh-groups";

export const listCshGroupSummaries = (): Promise<ApiResponse<CshGroupSummary[]>> =>
  apiClient.get<CshGroupSummary[]>(BASE);

export const getCshGroupDetail = (groupId: string): Promise<ApiResponse<CshGroupDetail>> =>
  apiClient.get<CshGroupDetail>(`${BASE}/${encodeURIComponent(groupId)}`);

type CustomerGridItem = {
  customerId: number;
  customerName: string;
};

// List all CSH groups
export const listCshGroupsGrid = (): Promise<ApiResponse<CshGroup[]>> =>
  apiClient.get<CshGroup[]>(BASE);

// Create a new CSH group
export const createCshGroupGrid = (
  payload: { name: string }
): Promise<ApiResponse<{ id: string }>> =>
  apiClient.post<{ id: string }>(BASE, payload);

// Update a CSH group
export const updateCshGroupGrid = (
  id: string,
  payload: UpdateCshGroupRequest
): Promise<ApiResponse<CshGroup>> =>
  apiClient.put<CshGroup>(`${BASE}/${id}`, payload);

// Delete a CSH group
export const deleteCshGroupGrid = (
  id: string
): Promise<ApiResponse<unknown>> =>
  apiClient.delete<unknown>(`${BASE}/${id}`);

// Get group details
export const getGroupDetailsGrid = (
  groupId: string
): Promise<ApiResponse<GroupDetails>> =>
  apiClient.get<GroupDetails>(`${BASE}/${groupId}`);

// Get all users
export const getUsersGrid = (): Promise<ApiResponse<AssignedUserSelection[]>> =>
  apiClient.get<AssignedUserSelection[]>(USERS_URL);

// Get all customers
export const getCustomerGrid = (): Promise<ApiResponse<CustomerGridItem[]>> =>
  apiClient.get<CustomerGridItem[]>(`${CUSTOMER_URL}/all`);

// Get sites by customer ID
export const getSitesByCustomerGrid = (
  customerId: number
): Promise<ApiResponse<CshGroupSite[]>> =>
  apiClient.get<CshGroupSite[]>(`${CUSTOMER_URL}/customers/${customerId}/sites`);

// Backwards-compatible simple names
export const getSitesByCustomer = getSitesByCustomerGrid;
export const getUsers = getUsersGrid;
export const getCustomer = getCustomerGrid;

// Assign users to a group
export const assignUsersToGroupGrid = (
  groupId: string,
  userIds: string[]
): Promise<ApiResponse<{ success: boolean }>> =>
  apiClient.post<{ success: boolean }>(
    `${BASE}/${groupId}/users`,
    { userIds }
  );

// Assign sites to a group
export const assignSitesToGroupGrid = (
  groupId: string,
  siteIds: string[]
): Promise<ApiResponse<{ success: boolean }>> =>
  apiClient.post<{ success: boolean }>(
    `${BASE}/${groupId}/sites`,
    { siteIdentifierRegistryIds: siteIds }
  );

// Remove users from a group
export const removeUsersFromGroupGrid = (
  groupId: string,
  userIds: string[]
): Promise<ApiResponse<{ success: boolean }>> =>
  apiClient.delete<{ success: boolean }>(
    `${BASE}/${groupId}/users`,
    { data: { userIds } }
  );

// Remove sites from a group
export const removeSitesFromGroupGrid = (
  groupId: string,
  siteIds: string[]
): Promise<ApiResponse<{ success: boolean }>> =>
  apiClient.delete<{ success: boolean }>(
    `${BASE}/${groupId}/sites`,
    { data: { siteIdentifierRegistryIds: siteIds } }
  );

// Backwards-compatible aliases for legacy import names
export const assignUsersToGroup = assignUsersToGroupGrid;
export const assignSitesToGroup = assignSitesToGroupGrid;
export const getGroupDetails = getGroupDetailsGrid;
export const removeUsersFromGroup = removeUsersFromGroupGrid;
export const removeSitesFromGroup = removeSitesFromGroupGrid;

// Download helpers — use fetch to avoid Axios blob/error confusion
async function downloadGroupFile(url: string, fallbackFilename: string): Promise<void> {
  const { getStoredToken } = await import('../http/httpClient');
  const headers: Record<string, string> = {};
  const token = await getStoredToken();
  if (token) { headers['Authorization'] = `Bearer ${token}`; }

  const res = await fetch(url, { headers });

  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const text = await res.text();
      if (text) { detail = text; }
    } catch { /* ignore */ }
    throw new Error(`Download failed: ${detail}`);
  }

  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
  const filename = match?.[1]?.replace(/['"]/g, '') ?? fallbackFilename;

  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

export function downloadCshGroupSites(groupId: string): Promise<void> {
  return downloadGroupFile(
    `${BASE}/${encodeURIComponent(groupId)}/download/sites`,
    `csh-group-sites-${groupId}-${new Date().toISOString().slice(0, 10)}.csv`
  );
}

export function downloadCshGroupUsers(groupId: string): Promise<void> {
  return downloadGroupFile(
    `${BASE}/${encodeURIComponent(groupId)}/download/users`,
    `csh-group-users-${groupId}-${new Date().toISOString().slice(0, 10)}.csv`
  );
}

// In-memory fallback data for dev / tests — preserved from earlier mock implementation
const now = new Date().toISOString();
let mockGroups: CshGroup[] = [
  {
    id: String(1),
    name: "Group Alpha",
    assignedTo: ["John Doe"],
    activeAlarms: 2,
    customers: [
      {
        customerId: 1,
        customerName: "Acme Corp",
        sites: [{ siteId: 101, siteName: "Site A" }, { siteId: 102, siteName: "Site B" }],
      },
    ],
    createdAt: now,
    updatedAt: now,
  },
  {
    id: String(2),
    name: "Group Beta",
    assignedTo: ["Jane Doe"],
    activeAlarms: 0,
    customers: [
      {
        customerId: 2,
        customerName: "Globex",
        sites: [{ siteId: 201, siteName: "Site X" }],
      },
    ],
    createdAt: now,
    updatedAt: now,
  },
];

// --- API-backed functions (return raw data expected by callers) ---
export async function fetchCshSidebarGroups(options?: { includeSiteCounts?: boolean; groupIds?: Array<number | string> }): Promise<CshSidebarGroup[]> {
  try {
    const params = new URLSearchParams();
    if (options?.includeSiteCounts) {
      params.set('includeSiteCounts', 'true');
    }
    if (Array.isArray(options?.groupIds) && options.groupIds.length > 0) {
      const normalized = options.groupIds
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && id > 0)
        .map(id => String(id));
      if (normalized.length > 0) {
        params.set('groupIds', normalized.join(','));
      }
    }
    const query = params.toString();
    const url = query ? `${BASE}/sidebar?${query}` : `${BASE}/sidebar`;
    const res = await apiService.get<CshSidebarGroup[]>(url);
    if (res?.success && Array.isArray(res.data)) {
      return res.data;
    }
  } catch (error) {
    console.info("Failed to fetch CSH sidebar groups", error);
  }
  return [];
}

export async function fetchCshGroups(): Promise<CshGroupSummary[]> {
  try {
    const res = await apiService.get<CshGroupSummary[]>(BASE);
    if (res?.success && Array.isArray(res.data)) {
      return res.data;
    }
  } catch (error) {
    console.info("Failed to fetch CSH groups", error);
  }
  return [];
}

export async function fetchCshGroupAlarms(groupId: number | string): Promise<CshGroupAlarm[]> {
  try {
    const res = await apiService.get<CshGroupAlarm[]>(`${BASE}/${encodeURIComponent(String(groupId))}/alarms`);
    if (res?.success && Array.isArray(res.data)) {
      return res.data;
    }
  } catch (error) {
    console.info('Failed to fetch CSH group alarms', error);
  }
  return [];
}

export async function fetchCshGroup(groupId: number | string): Promise<CshGroupDetail | null> {
  try {
    const res = await apiService.get<CshGroupDetail>(`${BASE}/${encodeURIComponent(String(groupId))}`);
    if (res?.success && res.data) {
      return res.data;
    }
  } catch (error) {
    console.info("Failed to fetch CSH group detail", error);
  }
  return null;
}

// --- Management CRUD functions: prefer API but fallback to in-memory mock to preserve previous behavior ---
export async function listCshGroups(): Promise<CshGroup[]> {
  try {
    const res = await apiService.get<CshGroup[]>(BASE);
    if (res?.success && Array.isArray(res.data)) {
      return res.data;
    }
  } catch (error) {
    console.info("Failed to list CSH groups", error);
  }
  return [];
}

export async function createCshGroup(payload: CreateCshGroupRequest): Promise<CshGroup> {
  try {
    const res = await apiService.post<CshGroup>(BASE, payload);
    if (res?.success && res.data) {
      return res.data;
    }
  } catch (error) {
    console.info("Failed to create CSH group", error);
  }

  const createdAt = new Date().toISOString();
  const newGroup: CshGroup = {
    id: String(Date.now()),
    name: payload.name,
    assignedTo: [payload.assignedTo],
    customers: payload.customers ?? [],
    activeAlarms: 0,
    updatedAt: createdAt,
    createdAt,
  };
  mockGroups.unshift(newGroup);
  return newGroup;
}

export async function updateCshGroup(id: string | number, payload: UpdateCshGroupRequest): Promise<CshGroup> {
  try {
    const res = await apiService.put<CshGroup>(`${BASE}/${encodeURIComponent(String(id))}`, payload);
    if (res?.success && res.data) {
      return res.data;
    }
  } catch (error) {
    console.info("Failed to update CSH group", error);
  }

  const idx = mockGroups.findIndex(g => g.id === String(id));
  if (idx === -1) {
    throw new Error("Group not found");
  }
  const updated: CshGroup = {
    ...mockGroups[idx],
    name: payload.name,
    assignedTo: [payload.assignedTo],
    customers: payload.customers ?? [],
    updatedAt: new Date().toISOString(),
  };
  mockGroups[idx] = updated;
  return updated;
}

export async function deleteCshGroup(id: string | number): Promise<void> {
  try {
    const res = await apiService.delete<void>(`${BASE}/${encodeURIComponent(String(id))}`);
    if (res?.success) {
      return;
    }
  } catch (error) {
    console.info("Failed to delete CSH group", error);
  }

  mockGroups = mockGroups.filter(g => g.id !== String(id));
}

export default {
  fetchCshSidebarGroups,
  fetchCshGroups,
  fetchCshGroup,
  listCshGroups,
  createCshGroup,
  updateCshGroup,
  deleteCshGroup,
};
